import React, { useState, useCallback, useContext, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import _, { get } from "lodash";
import dateFormat from "dateformat";
import { useQueryClient } from "@tanstack/react-query";
import { guid } from "src/utils/util";
import { Image } from "react-bootstrap";

import { AppContext } from "src/components/app/contexts/appContext";
import { parseMessageContent } from "src/pages/dealRoom/components/chat/chatContainer/chatSection/messageComponent";
import {
  useSmallTalkMessagesQuery,
  useSendSmallTalkMessageMutation,
  useSmallTalkUuid,
  smallTalkKeys,
  type SmallTalkClient,
} from "src/redux/actions/smallTalk";
import {
  useAssociateChatUserMutation,
  useBlockChatUserMutation,
  useChatUserQuery,
  useCurrentlyViewingAssetQuery,
  useSendMessageAsEmailMutation,
} from "src/redux/actions/chatUsers";
import { useEngageContext } from "src/pages/engage/context/EngageContext";
import Actions from "src/components/actionbar";
import Slider from "src/components/slider";
import Button from "src/components/button";
import Contact from "src/components/contact";
import Tooltip from "src/components/tooltip";
import LineEllipsis from "react-lines-ellipsis";

import styles from "./chatContainer.module.scss";
import cx from "classnames";
import EmptyMessage from "src/components/emptyMessage";
import userRoleService from "src/utils/userRoles.service";


const MESSAGE_LIMIT = 100;
const DefaultAssetIcon = "/images/defaultIcons/default_asset.jpg";
const DefaultUserAvatar = "/images/default_user_profile_background.png";

interface ChatUser {
  id?: string | number;
  type?: string;
  uuid?: string;
  firstName?: string;
  lastName?: string;
  lastOnline?: string | number;
  online?: boolean;
  blocked?: boolean;
  chatUserId?: string | number;
  contactDTO?: { id?: string; icon?: { thumbnail?: string } };
  userAssociations?: any[];
  [key: string]: unknown;
}

type NormalizedMessage = {
  id: unknown;
  text: string;
  sender: { id?: unknown; name?: string; type?: string };
  timestamp: number;
  dealRoomId?: string;
};

type Participant = {
  id?: unknown;
  name?: string | null;
  firstName?: string;
  icon?: { thumbnail?: string };
};

interface MaskConfig {
  title: string;
  icon: string;
  message: string[];
  buttonText: string;
  buttonIcon: string;
  onClick: () => void;
}

function buildChannel(
  chatUser: ChatUser,
  accountId: string | number | undefined
): string | null {
  if (chatUser.uuid) return chatUser.uuid;
  if (accountId == null) return null;
  return chatUser.type === "contact"
    ? `a_${accountId}.c_${chatUser.id}`
    : `a_${accountId}.v_${chatUser.id}`;
}

function toDateKey(ts: number): string {
  const d = new Date(ts / 10000);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function todayKey(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function yesterdayKey(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function getDateHeading(dateKey: string): string {
  if (dateKey === todayKey()) return "Today";
  if (dateKey === yesterdayKey()) return "Yesterday";
  const [y, m, d] = dateKey.split("-");
  return dateFormat(new Date(Number(y), Number(m) - 1, Number(d)), "mmm d, yyyy");
}

function formatTime24(ts: number): string {
  return ts ? dateFormat(new Date(ts / 10000), "HH:MM") : "";
}

function formatLastOnline(timestamp: string | number | undefined): string {
  const value = timestamp != null ? Number(timestamp) : NaN;
  if (!Number.isFinite(value)) return "";

  const diffMs = Date.now() - value;
  if (diffMs < 0) return "";

  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"}`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"}`;
  return "";
}

function getParticipantFirstName(participant: Participant): string {
  return participant.firstName || participant.name?.split(" ")[0] || "";
}

function normalizeMessages(rawMessages: unknown[]): NormalizedMessage[] {
  return rawMessages.map((item, index) => {
    const record = item as Record<string, unknown>;
    const msg = get(record, "message", record) as Record<string, unknown>;
    const timetoken = get(record, "timetoken", 0) as number;
    return {
      id: get(msg, "id") ?? get(record, "message.id") ?? index,
      text: get(msg, "text", "") as string,
      sender: get(msg, "sender", { name: "", type: "user" }) as NormalizedMessage["sender"],
      timestamp: typeof timetoken === "number" ? timetoken : Number(timetoken) || 0,
      dealRoomId: get(msg, "dealRoomId", "") as string,
    };
  });
}

function groupMessagesByDate(
  messages: NormalizedMessage[]
): [string, NormalizedMessage[]][] {
  const map = new Map<string, NormalizedMessage[]>();
  for (const msg of messages) {
    const key = toDateKey(msg.timestamp);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(msg);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function useBlockUser(
  chatUser: ChatUser | null,
  setSelectedChatUser: (u: ChatUser) => void
) {
  const [showBlockUserSlider, setShowBlockUserSlider] = useState(false);
  const [isUnblockFlow, setIsUnblockFlow] = useState(false);
  const [userIsBlocked, setUserIsBlocked] = useState<boolean>(
    chatUser?.blocked ?? false
  );

  const blockUserMutation = useBlockChatUserMutation();

  useEffect(() => {
    setUserIsBlocked(chatUser?.blocked ?? false);
    setShowBlockUserSlider(false);
    setIsUnblockFlow(false);
  }, [chatUser]);

  const openBlockSlider = useCallback(() => {
    setIsUnblockFlow(false);
    setShowBlockUserSlider(true);
  }, []);

  const openUnblockSlider = useCallback(() => {
    setIsUnblockFlow(true);
    setShowBlockUserSlider(true);
  }, []);

  const closeBlockSlider = useCallback(() => {
    setShowBlockUserSlider(false);
    setIsUnblockFlow(false);
  }, []);

  const confirmBlockToggle = useCallback(() => {
    const chatUserId = (chatUser?.id ?? chatUser?.chatUserId) as
      | string
      | number
      | undefined;
    if (chatUserId == null) return;

    const newBlockedState = !isUnblockFlow;
    blockUserMutation.mutate(
      { chatUserId, blocked: newBlockedState },
      {
        onSuccess: () => {
          setUserIsBlocked(newBlockedState);
          setShowBlockUserSlider(false);
          setIsUnblockFlow(false);
          if (chatUser) setSelectedChatUser({ ...chatUser, blocked: newBlockedState });
        },
      }
    );
  }, [chatUser, isUnblockFlow, blockUserMutation, setSelectedChatUser]);

  return {
    showBlockUserSlider,
    isUnblockFlow,
    userIsBlocked,
    isPending: blockUserMutation.isPending,
    openBlockSlider,
    openUnblockSlider,
    closeBlockSlider,
    confirmBlockToggle,
  };
}

function useNormalizedMessages(rawMessages: unknown[]) {
  return useMemo(() => {
    const messages = normalizeMessages(rawMessages);
    const messagesByDate = groupMessagesByDate(messages);
    return { messages, messagesByDate };
  }, [rawMessages]);
}

function useParticipants(
  chatUser: ChatUser | null
) {
  return useMemo((): Participant[] => {
    const chatUserAsParticipant: Participant | null =
      chatUser != null
        ? {
            id: chatUser.id ?? chatUser.chatUserId,
            name: `${chatUser.firstName ?? ""} ${chatUser.lastName ?? ""}`.trim() || undefined,
            firstName: chatUser.firstName,
            icon: chatUser.contactDTO?.icon as { thumbnail?: string } | undefined,
          }
        : null;

    const associatedUsers: Participant[] =
      chatUser?.userAssociations?.map((user: { id?: unknown; firstName?: string; lastName?: string; icon?: { thumbnail?: string } }) => ({
        id: user.id,
        name: [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || undefined,
        firstName: user.firstName,
        icon: user.icon,
      })) ?? [];

    const allParticipants = [...(chatUserAsParticipant ? [chatUserAsParticipant] : []), ...associatedUsers];

    const uniqueParticipants = _.uniqBy(allParticipants, "id");

    return uniqueParticipants;
  }, [chatUser]);
}

function useChatScroll(chatUserId: string | number | undefined, messagesLength: number) {
  const ref = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatUserId, messagesLength, scrollToBottom]);

  return ref;
}

function ChatHeaderAvatar({
  chatUser,
  participants,
}: {
  chatUser: ChatUser;
  participants: Participant[];
}) {
  const isGroup = participants.length > 2;

  const profileClasses = [styles.profile1, styles.profile2, styles.profile3];
  const topThree = participants.slice(0, 3).map((p) => ({
    id: p.id,
    firstName: getParticipantFirstName(p),
  }));
  const showDefaultInThird = participants.length === 2;
  const displayProfiles = showDefaultInThird
    ? [...topThree, { id: "default", firstName: "" }]
    : topThree;

  if (isGroup) {
    return (
      <div className={styles.groupProfiles}>
        {displayProfiles.slice(0, 3).map((profile, index) =>
          showDefaultInThird && index === 2 ? (
            <div key="default-avatar" className={profileClasses[index]}>
              <Image src={DefaultUserAvatar} roundedCircle />
            </div>
          ) : (
            <div
              key={String(profile.id ?? profile.firstName)}
              className={profileClasses[index]}
              title={profile.firstName}
            >
              {profile.firstName.charAt(0) || "?"}
            </div>
          )
        )}
      </div>
    );
  }

  if (chatUser.type === "visitor") {
    return (
      <div className={styles.chatHeaderLeftNameAvatar}>
        <span className="paperflite-user-settings" />
      </div>
    );
  }

  const thumbnail = chatUser.contactDTO?.icon?.thumbnail;
  return (
    <>
      {thumbnail ? (
        <Image
          className={styles.chatHeaderLeftAvatar}
          src={thumbnail}
          roundedCircle
          alt="chat user avatar"
        />
      ) : (
        <div className={styles.chatHeaderLeftNameAvatar}>
          {(chatUser.firstName ?? "").charAt(0)}
          {(chatUser.lastName ?? "").charAt(0)}
        </div>
      )}
      {chatUser.online === true && <span className={styles.onlineIndicator} />}
    </>
  );
}

function MessageBubble({
  chat,
  isCurrentUser,
  avatarSrc,
  avatarInitials,
  avatarDisplayName,
}: {
  chat: NormalizedMessage;
  isCurrentUser: boolean;
  avatarSrc: string | null;
  avatarInitials: string;
  avatarDisplayName: string;
}) {
  const isUser = chat.sender?.type === "user";

  return (
    <div
      className={cx(styles.chatBodyMessage, {
        [styles.chatBodyMessageRight]: isUser,
      })}
    >
      <div className={styles.chatBodyMessageInner}>
        <Tooltip content={<span>{avatarDisplayName}</span>} placement="top">
          <div className={styles.chatBodyMessageAvatar}>
            {avatarSrc ? (
              <Image className={styles.chatUserIcon} src={avatarSrc} roundedCircle />
            ) : (
              avatarInitials
            )}
          </div>
        </Tooltip>
        <div
          className={cx(styles.chatBodyMessageBubble, {
            [styles.chatBodyMessageBubbleRight]: isUser,
          })}
        >
          <span className={styles.chatBodyMessageText}>{parseMessageContent(chat.text, chat.dealRoomId ?? '')}</span>
          {chat.timestamp ? (
            <span className={styles.chatBodyMessageTime}>
              {formatTime24(chat.timestamp)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function BlockSlider({
  chatUser,
  show,
  isUnblockFlow,
  isPending,
  canBlock,
  onConfirm,
  onClose,
}: {
  chatUser: ChatUser;
  show: boolean;
  isUnblockFlow: boolean;
  isPending: boolean;
  canBlock: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Slider.Dialog width="300" show={show} onClose={onClose}>
      <Slider.Header
        title={isUnblockFlow ? "unblock recipient" : "block recipient"}
        message={
          isUnblockFlow
            ? "Are you sure you would like to unblock the selected recipient?"
            : "Are you sure you would like to block the selected recipient?"
        }
      />
      <Slider.Body>
        <div className={styles.blockUserContent}>
          <div className={styles.blockUserIcon}>
            {chatUser.contactDTO?.icon?.thumbnail ? (
              <Image
                className={styles.chatUserIcon}
                src={chatUser.contactDTO.icon.thumbnail}
                roundedCircle
              />
            ) : (
              <>
                {(chatUser.firstName ?? "").charAt(0)}
                {(chatUser.lastName ?? "").charAt(0)}
              </>
            )}
          </div>
          <div className={styles.blockUserDetails}>
            <div className={styles.blockUserDetailsName}>
              {chatUser.firstName} {chatUser.lastName}
            </div>
            {get(chatUser, "country.city", "") as string && get(chatUser, "country.country_name", "") as string && <div className={styles.blockUserDetailsLocation}>
              <i className={cx("paperflite-location", styles.locationIcon)} />
              <span className={styles.locationText}>
                {get(chatUser, "country.city", "") as string},{" "}
                {get(chatUser, "country.country_name", "") as string}
              </span>
            </div>}
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={onConfirm}
          disabled={isPending}
        />
        <Button variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

function ChatMask({
  config,
  userIsBlocked,
}: {
  config: MaskConfig;
  userIsBlocked: boolean;
}) {
  const showButton = userIsBlocked ? userRoleService.canBlockRecipient() : true;

  return (
    <div className={styles.blockedMask} aria-live="polite">
      <span className={styles.blockedMaskHeader}>
        <i className={cx(config.icon, styles.blockedMaskIcon)} />
        <span className={styles.blockedMaskTitle}>{config.title}</span>
      </span>
      <div className={styles.blockedMaskMessage}>
        {config.message.map((line, index) => (
          <span key={index}>{line}</span>
        ))}
      </div>
      {showButton && (
        <Button
          variant="primary"
          icon={config.buttonIcon}
          text={config.buttonText}
          onClick={config.onClick}
          className={styles.blockedMaskButton}
        />
      )}
    </div>
  );
}

export function ChatContainer({
  isLoading,
  chatUser,
  showLiveViewSlider,
  setShowLiveViewSlider,
}: {
  isLoading: boolean;
  chatUser: ChatUser | null;
  showLiveViewSlider: boolean;
  setShowLiveViewSlider: (show: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { smallTalk } = useContext(AppContext);
  const client = (smallTalk?.client ?? null) as SmallTalkClient | null;

  const account = useSelector((state: { accounts?: { item?: { id?: string | number } } }) =>
    get(state, "accounts.item")
  );
  const accountId = get(account, "id") as string | number | undefined;

  const user = useSelector(
    (state: { users?: { me?: { id?: string | number; firstName?: string; lastName?: string } } }) =>
      get(state, "users.me")
  );

  const uuid = useSmallTalkUuid();
  const { setSelectedChatUser, setCurrentlyViewingAsset } = useEngageContext();


  const [message, setMessage] = useState("");
  const [startChat, setStartChat] = useState(false);
  const [showContactSlider, setShowContactSlider] = useState(false);
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [headerNameClamped, setHeaderNameClamped] = useState(false);
  const [assetNameClamped, setAssetNameClamped] = useState(false);
  const [participantNameClamped, setParticipantNameClamped] = useState(false);


  useEffect(() => {
    setShowContactSlider(false);
    setShowAllParticipants(false);
    setStartChat(false);
    setMessage("");
  }, [chatUser]);


  const channel = chatUser != null ? buildChannel(chatUser, accountId) : null;
  const chatUserId = chatUser != null
    ? (chatUser.id ?? chatUser.chatUserId) as string | number | undefined
    : undefined;

  const { data: chatUserDetail } = useChatUserQuery(chatUserId ?? null);
  const chatUserForParticipants: ChatUser | null =
    chatUser == null
      ? null
      : chatUserDetail != null
        ? { ...chatUser, ...chatUserDetail }
        : chatUser;

  const {
    data: rawMessages = [],
    isLoading: messagesLoading,
    isError: messagesError,
    refetch: refetchMessages,
  } = useSmallTalkMessagesQuery({ client, channel, limit: MESSAGE_LIMIT });

  useEffect(() => {
    if (client && channel) {
      void refetchMessages();
    }
  }, [client, channel, refetchMessages]);

  const { data: currentlyViewingAsset } = useCurrentlyViewingAssetQuery(chatUserId ?? null);

  const sendMutation = useSendSmallTalkMessageMutation();
  const sendMessageAsEmailMutation = useSendMessageAsEmailMutation();
  const associateChatUserMutation = useAssociateChatUserMutation();

  const block = useBlockUser(chatUser, setSelectedChatUser);
  const { messages: normalizedMessages, messagesByDate } = useNormalizedMessages(rawMessages);
  const participants = useParticipants(chatUserForParticipants);
  const messagesContainerRef = useChatScroll(chatUserId, normalizedMessages.length);

  useEffect(() => {
    if (chatUser == null) {
      setCurrentlyViewingAsset(null);
    } else if (currentlyViewingAsset !== undefined) {
      setCurrentlyViewingAsset(currentlyViewingAsset ?? null);
    }
  }, [chatUser, currentlyViewingAsset, setCurrentlyViewingAsset]);

  const chatUserWithAsset = useMemo(
    () =>
      chatUser != null && currentlyViewingAsset !== undefined
        ? { ...chatUser, currentlyViewingAsset }
        : chatUser,
    [chatUser, currentlyViewingAsset]
  );


  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = message.trim();
      if (!text || !client || !channel || !chatUser || !uuid) return;

      const isOffline = chatUser.online === false;
      const isContact = chatUser.type === "contact";

      if (get(chatUser, "associated") === false && chatUserId != null && uuid) {
        associateChatUserMutation.mutate({
          chatUserId: chatUserId as string | number,
          uuid: String(channel),
        });
      }
      if (isOffline && isContact && chatUserId != null) {
        sendMessageAsEmailMutation.mutate({
          chatUserId: chatUserId as string | number,
          message: text,
        });
      }

      sendMutation.mutate(
        {
          client,
          message: {
            channel,
            message: {
              id: guid(),
              type: "MESSAGE",
              text,
              timestamp: Date.now(),
              fingerPrint: "",
              sharedUrl: "",
              sender: {
                chatUser: chatUser.id,
                id: user?.id,
                name: `${get(user, "firstName", "")} ${get(user, "lastName", "")}`.trim(),
                type: "user",
              },
            },
            uuid,
          },
        },
        {
          onSuccess: () => {
            setMessage("");
            queryClient.invalidateQueries({ queryKey: smallTalkKeys.messages(channel) });
          },
        }
      );
    },
    [message, client, channel, chatUser, chatUserId, uuid, user, sendMutation, sendMessageAsEmailMutation, associateChatUserMutation, queryClient]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e as unknown as React.FormEvent);
      }
    },
    [handleSubmit]
  );


  const maskConfig = useMemo<MaskConfig | null>(() => {
    if (!chatUser || startChat) return null;

    if (block.userIsBlocked) {
      return {
        title: "Recipient Blocked",
        icon: "paperflite-unblock",
        message: [
          "This recipient has been blocked",
          userRoleService.canBlockRecipient()
            ? "If you've changed your mind, hit the Unblock button below."
            : "",
        ],
        buttonText: "Unblock",
        buttonIcon: "paperflite-unblock",
        onClick: block.openUnblockSlider,
      };
    }

    if (chatUser.online === false && normalizedMessages.length > 0) {
      return {
        title: "Currently Offline",
        icon: "paperflite-offline",
        message: ["You can still leave a message and keep things going."],
        buttonText: "Start Chat",
        buttonIcon: "paperflite-tick",
        onClick: () => setStartChat(true),
      };
    }

    return null;
  }, [chatUser, startChat, block.userIsBlocked, block.openUnblockSlider, normalizedMessages.length]);

  const isGroupChat = participants.length > 2;

  const headerDisplayText = isGroupChat
    ? (() => {
        const firstTwo = participants
          .slice(0, 2)
          .map((p) => getParticipantFirstName(p))
          .filter(Boolean)
          .join(", ");
        const restCount = Math.max(0, participants.length - 2);
        return restCount > 0
          ? `${firstTwo} + ${restCount} more`
          : firstTwo || `${chatUser?.firstName ?? ""} ${chatUser?.lastName ?? ""}`.trim();
      })()
    : `${chatUser?.firstName ?? ""} ${chatUser?.lastName ?? ""}`.trim();

  const headerNamesPart = isGroupChat
    ? participants
        .slice(0, 2)
        .map((p) => getParticipantFirstName(p))
        .filter(Boolean)
        .join(", ") || `${chatUser?.firstName ?? ""} ${chatUser?.lastName ?? ""}`.trim()
    : null;

  const headerMorePart =
    isGroupChat && participants.length > 2 ? `+ ${participants.length - 2} more` : null;

  const showLastOnline =
    chatUser?.lastOnline != null &&
    formatLastOnline(chatUser.lastOnline) !== "";

  if (!chatUser && normalizedMessages.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyMessageContainer}>
        <EmptyMessage
          image="/images/engage/chat.svg"
          heading="Enable engage to start conversations"
        />
      </div>
    );
  }

  if (!chatUser && isLoading) {
    return (
      <div className={cx(styles.placeholderChatContainer, styles.placeholder)}>
        <div className={cx(styles.placeholderChatHeader, styles.placeholder)}>
          <div className={cx(styles.placeholderChatHeaderAvatar, styles.block)} />
          <div className={cx(styles.placeholderChatHeaderName, styles.block)} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      <>
      <div className={styles.chatHeaderParticipantsWrapper}>
        <div className={styles.chatHeader}>
          <div
            className={cx(styles.chatHeaderLeft, {
              [styles.disabled]: chatUser?.type === "visitor",
            })}
            onClick={() => setShowContactSlider(true)}
          >
            <div className={styles.chatAvatarContainer}>
              {chatUser && (
                <ChatHeaderAvatar chatUser={chatUser} participants={participants} />
              )}
            </div>

            <div className={styles.chatHeaderLeftNameContainer}>
              <div className={styles.chatHeaderLeftName}>
                {headerMorePart != null ? (
                  <>
                    <Tooltip
                      content={headerDisplayText}
                      placement="right"
                      disabled={!headerNameClamped}
                    >
                      <span className={styles.chatHeaderLeftNameTruncate}>
                        <LineEllipsis
                          text={headerNamesPart ?? ""}
                          maxLine="1"
                          ellipsis="..."
                          trimRight
                          basedOn="words"
                          onReflow={({ clamped }) => setHeaderNameClamped(clamped)}
                        />
                      </span>
                    </Tooltip>
                    <span className={styles.chatHeaderLeftNameMore}>{headerMorePart}</span>
                  </>
                ) : (
                  <Tooltip
                    content={headerDisplayText}
                    placement="right"
                    disabled={!headerNameClamped}
                  >
                    <LineEllipsis
                      text={headerDisplayText}
                      maxLine="1"
                      ellipsis="..."
                      trimRight
                      basedOn="words"
                      onReflow={({ clamped }) => setHeaderNameClamped(clamped)}
                    />
                  </Tooltip>
                )}
              </div>

              {(get(chatUser, "country.city") as string) &&
                (get(chatUser, "country.country_name") as string) && (
                  <div className={styles.chatHeaderLeftDetails}>
                    <i className={cx("paperflite-location", styles.locationIcon)} />
                    <span className={styles.locationText}>
                      {get(chatUser, "country.city", "") as string},{" "}
                      {get(chatUser, "country.country_name", "") as string}
                    </span>
                  </div>
                )}
            </div>

            {isGroupChat && (
              <i
                className={cx("paperflite-dropdown", styles.showAllParticipantsIcon, {
                  [styles.hideAllParticipantsIcon]: showAllParticipants,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllParticipants((prev) => !prev);
                }}
              />
            )}
          </div>

          <div className={styles.chatHeaderRight}>
            <div className={styles.chatHeaderRightActions}>
              {!block.userIsBlocked && userRoleService.canBlockRecipient() && (
                <Actions.Container>
                  <Actions.Row>
                    <Actions.IconButton
                      icon="paperflite-block"
                      tooltip="Block recipient"
                      onClick={block.openBlockSlider}
                    />
                  </Actions.Row>
                </Actions.Container>
              )}
            </div>
          </div>
        </div>

        <div
          className={cx(styles.chatParticipants, {
            [styles.showAllParticipants]: showAllParticipants,
          })}
        >
          {participants.map((participant) => (
            <div key={String(participant.id)} className={styles.chatParticipant}>
              <div className={styles.chatParticipantAvatar}>
                {participant.icon?.thumbnail ? (
                  <Image
                    className={styles.chatParticipantAvatarImage}
                    src={participant.icon.thumbnail}
                    roundedCircle
                    alt="chat user avatar"
                  />
                ) : (
                  <div className={styles.chatParticipantAvatarInitials}>
                    {participant.name?.charAt(0) ?? ""}
                  </div>
                )}
              </div>
              <div className={styles.chatParticipantName}>
                <Tooltip
                  content={participant.name}
                  placement="top"
                  disabled={!participantNameClamped}
                >
                  <LineEllipsis
                    text={participant.name ?? ""}
                    maxLine="1"
                    ellipsis="..."
                    trimRight
                    basedOn="letters"
                    onReflow={({ clamped }) => setParticipantNameClamped(clamped)}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div ref={messagesContainerRef} className={styles.chatBody}>
        <div className={styles.chatBodyMessages}>
          {messagesLoading && (
            <>
              {[0, 1].map((i) => (
                <div key={i} className={cx(styles.placeholderChatBodyMessage, styles.placeholder)}>
                  <div className={cx(styles.placeholderChatBodyMessageAvatar, styles.block)} />
                  <div className={cx(styles.placeholderChatBodyMessageName, styles.block)} />
                </div>
              ))}
            </>
          )}

          {messagesError && (
            <div className={styles.chatBodyMessage}>Failed to load messages.</div>
          )}

          {!messagesLoading && !messagesError && normalizedMessages.length === 0 && (
            <div className={styles.emptyMessageContainer}>
              <div className={styles.contactAvatarContainer}>
                <div className={styles.contactAvatar}>
                  {chatUser?.contactDTO?.icon?.thumbnail ? (
                    <Image
                      className={styles.contactAvatarImage}
                      src={chatUser.contactDTO.icon.thumbnail}
                      roundedCircle
                      alt="chat user avatar"
                    />
                  ) : (
                    <div className={styles.chatHeaderLeftNameAvatar}>
                      {(chatUser?.firstName ?? "").charAt(0)}
                      {(chatUser?.lastName ?? "").charAt(0)}
                    </div>
                  )}
                </div>
                {showLastOnline && (
                  <div className={styles.lastOnline}>
                    <span className={styles.lastOnlineText}>
                      {formatLastOnline(chatUser?.lastOnline)}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.emptyMessage}>
                <p className={styles.emptyMessageContent}>It seems quiet here.</p>
                <p className={styles.emptyMessageContent}>
                  Kick things off by starting a conversation.
                </p>
              </div>
            </div>
          )}

          {!messagesLoading &&
            !messagesError &&
            messagesByDate.map(([dateKey, chats]) => (
              <React.Fragment key={dateKey}>
                <div className={styles.chatBodyDateHeading}>{getDateHeading(dateKey)}</div>
                {chats.map((chat) => {
                  const isCurrentUser = String(chat.sender?.id) === String(user?.id);
                  const isUser = chat.sender?.type === "user";
                  const avatarSrc = (
                    isCurrentUser
                      ? get(user, "icon.thumbnail")
                      : chatUser != null &&
                        !isUser &&
                        (chatUser.type === "contact" || chatUser.type === "visitor")
                      ? chatUser.contactDTO?.icon?.thumbnail
                      : null
                  ) as string | null;

                  const avatarInitials = isCurrentUser
                    ? (get(user, "firstName", "") as string).charAt(0)
                    : (chat.sender?.name ?? "").charAt(0) ?? "";

                  const avatarDisplayName =
                    String(chat.sender?.name ?? "") ||
                    (isCurrentUser
                      ? String(get(user, "name", ""))
                      : String(get(chatUser, "contactDTO.name", "")));

                  return (
                    <MessageBubble
                      key={String(chat.id)}
                      chat={chat}
                      isCurrentUser={isCurrentUser}
                      avatarSrc={avatarSrc}
                      avatarInitials={avatarInitials}
                      avatarDisplayName={avatarDisplayName}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          <div />
        </div>
      </div>


      {chatUserWithAsset?.currentlyViewingAsset &&
        !maskConfig &&
        !startChat &&
        chatUser?.online && (
          <div
            className={styles.liveViewUpdated}
            onClick={() => setShowLiveViewSlider(!showLiveViewSlider)}
          >
            <span className={styles.liveViewUpdatedContent}>
              <Image
                className={styles.liveViewUpdatedImage}
                src={
                  get(
                    chatUserWithAsset,
                    "currentlyViewingAsset.asset.icon.full",
                    DefaultAssetIcon
                  ) as string
                }
              />
              <span className={styles.liveViewUpdatedText}>Currently on</span>
              <span className={styles.liveUpdatedAssetName}>
                <Tooltip
                  content={get(chatUserWithAsset, "currentlyViewingAsset.asset.name", "") as string}
                  placement="top"
                  disabled={!assetNameClamped}
                >
                  <span className={styles.liveUpdatedAssetNameInner}>
                    <LineEllipsis
                      text={get(chatUserWithAsset, "currentlyViewingAsset.asset.name", "") as string}
                      maxLine="1"
                      ellipsis="..."
                      trimRight
                      basedOn="words"
                      onReflow={({ clamped }) => setAssetNameClamped(clamped)}
                    />
                  </span>
                </Tooltip>
              </span>
            </span>
          </div>
        )}

      <form onSubmit={handleSubmit}>
        <div
          className={cx(styles.inputContainer, {
            [styles.hideInputContainer]: maskConfig,
          })}
        >
          <div className={cx(styles.noPadding)}>
            <div className={styles.search}>
              <label>
                <textarea
                  className={cx(styles.field)}
                  name="search"
                  style={{ resize: "none" }}
                  value={message}
                  placeholder="Write a message..."
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={!client || !channel}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="submit"
                  disabled={
                    !message.trim() || sendMutation.isPending || !client || !channel
                  }
                  className={cx(styles.searchButton, {
                    [styles.searchButtonActive]: message.trim() !== "",
                  })}
                >
                  <i className="paperflite-right-arrow" />
                </button>
              </label>
            </div>
          </div>
        </div>
      </form>

      {maskConfig && (
        <ChatMask config={maskConfig} userIsBlocked={block.userIsBlocked} />
      )}

      <Contact
        show={chatUser?.type === "contact" && showContactSlider}
        contactId={chatUser?.contactDTO?.id ?? ""}
        onClose={() => setShowContactSlider(false)}
      />


      {chatUser && (
        <BlockSlider
          chatUser={chatUser}
          show={block.showBlockUserSlider}
          isUnblockFlow={block.isUnblockFlow}
          isPending={block.isPending}
          canBlock={userRoleService.canBlockRecipient()}
          onConfirm={block.confirmBlockToggle}
          onClose={block.closeBlockSlider}
        />
      )}
      </>
    </div>
  );
}